import { useEffect, useRef } from "react";
import { Type } from "@themes/pollinator/ui/Type";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { useMarketingTracking } from "../../marketing.analytics";

export type Props = {
  children: React.ReactNode;
  id?: string;
  title?: string;
  /**
   * Which way round the colours go. The design runs pale and dark in groups,
   * with each call to action in the opposite colour to what surrounds it.
   */
  tone?: "light" | "dark";
  /** A quote band is shallower than a full section, as the design draws it */
  size?: "regular" | "band";
  /** How far apart the blocks inside sit; the design closes some sections up */
  gap?: "regular" | "tight";
  /** Padding the design gives one section and not the rest */
  className?: string;
};

export function MarketingSection({
  children,
  id,
  title,
  tone = "light",
  size = "regular",
  gap = "regular",
  className,
}: Props) {
  const { trackSectionView } = useMarketingTracking();
  const reported = useRef(false);

  useEffect(() => {
    // Counted once per visit, when half the section is on screen. Firing on
    // mount would count sections nobody scrolled to, which would make the
    // funnel's first step meaningless.
    // Found by the anchor the section already renders with, rather than a ref:
    // the shared container does not forward one, and widening its API for a
    // measurement detail would be the wrong trade.
    if (!id || typeof IntersectionObserver === "undefined") return;
    const element = document.getElementById(id);
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !reported.current) {
            reported.current = true;
            trackSectionView(id);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [id, trackSectionView]);

  return (
    <OuterContentContainer
      as="section"
      id={id}
      className={`${
        tone === "dark" ? "bg-brand-800 text-brand-50" : "bg-brand-50 text-brand-800"
      } ${
        size === "band"
          ? "py-6 md:py-12 border-y border-brand-700"
          : "py-8 lg:py-16"
      } ${className ?? ""}`}
      // The design's column is 1248 wide with 96px margins at 1440, which is
      // this maximum less the container's own 32px of padding on each side.
      innerClassName="max-w-[1312px]"
    >
      <div
        className={`flex flex-col w-full ${
          gap === "tight" ? "gap-4 lg:gap-8" : "gap-6 lg:gap-8"
        }`}
      >
        {title && (
          <Type as="h2" variant="h2" className="max-w-6xl">
            {title}
          </Type>
        )}
        {children}
      </div>
    </OuterContentContainer>
  );
}

export default MarketingSection;
